import { ApiAction, ApiActionDisabledReason, ApiActionState, ApiFragment } from '@stylehaven/components';
import { BulkUserAction } from '../../../entities/common/bulk-user-action.entity';
import { ActionDisabledCondition, UserAction } from '../../../entities/common/user-action.entity';

/* Generic api action
 * Available when:
 *  Action is defined on the record
 *
 * Disabled when:
 *  * Action is disabled by engine
 *  * Action status is deferred and not in context of detail page
 */
export function GetGenericApiUserAction(
    record: ApiFragment,
    action: ApiAction,
    contextRecord?: ApiFragment
): UserAction {
    // Needed for bulk checking
    const localAction: ApiAction | undefined = record
        .getApiActions()
        .find(recordAction => recordAction.Rel === action.Rel);

    if (!localAction) {
        return new UserAction([false], []);
    }

    const isGenericDisabled: boolean = localAction.State === ApiActionState.Disallowed;
    const isDeferred: boolean = localAction.State === ApiActionState.Deferred;
    const isOnDetailPage: boolean = record.getIdentifier() === contextRecord?.getIdentifier();

    const reasons: ApiActionDisabledReason[] = localAction.Reasons ?? [];

    const availableConditions: boolean[] = [];
    let disableConditions: ActionDisabledCondition[];

    if (reasons.length > 0) {
        disableConditions = reasons.map(reason => ({
            condition: isGenericDisabled,
            tooltip: `generic.${reason.Code}`,
            tooltipFallback: reason.Value // Fall back to engine reason if frontend doesn't provide a translation
        }));
    } else {
        disableConditions = [
            {
                condition: isGenericDisabled,
                tooltip: ''
            }
        ];
    }

    if (action.Rel === 'approve_for_transfer') {
        disableConditions.push({ condition: !record.hasEditRights(), tooltip: 'generic.NoWriteRights' });
    }

    if (action.Rel === 'approve_for_transfer') {
        disableConditions.push({ condition: !record.hasExportRights(), tooltip: 'generic.NoExportRights' });
    }

    disableConditions.push({ condition: isDeferred && !isOnDetailPage, tooltip: 'generic.deferred' });

    return new UserAction(availableConditions, disableConditions);
}

/* Generic api action
 * Available when:
 * Shared across every selected record
 *
 * Disabled when:
 *  * Action is disabled or defered for one or more records
 */
export function GetBulkGenericApiUserAction(
    records: readonly ApiFragment[],
    action: ApiAction,
    contextRecord?: ApiFragment
): BulkUserAction {
    const isShared: boolean = records.every(
        record => !!record.getApiActions().find(recordAction => recordAction.Rel === action.Rel)
    );

    const availableConditions: boolean[] = [isShared];
    const disableConditions: ActionDisabledCondition[] = [];

    return new BulkUserAction(
        records,
        availableConditions,
        disableConditions,
        GetGenericApiUserAction,
        false,
        action,
        contextRecord
    );
}
